import { Injectable } from "@angular/core";
import { Cultivar, DetailRequest, Evaluation, EvaluationService, Filter, FilterBuilder, FilterPage, FilterQueryMode, FilterSort, GroupService, OrganizationGroup, Sample, SampleService, Site, Status, translateCommonErrorStatus } from "@core/data";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { SAMPLE_SORT_OPTIONS, SampleIndexQuery } from "../sample-index/sample-index.state-model";
import { LocalStorage } from "@core/browser";
import { isNumberRange, isNumberSet, parseNumberRangeValue, parseNumberSet } from "@core/utils";
import { tap } from "rxjs/operators";
import { forkJoin } from "rxjs";
import { Router } from "@angular/router";

export interface EvaluationTransferFormStateModel {
    orgKey: string;
    sampleKey: string
    evalKeys: string[];
    redirectKey: string;

    status: Status;
    data: Sample[];
    sample: Sample;
    groups: OrganizationGroup[];
    total: number;

    // filter options
    page: FilterPage;
    sort: FilterSort;
    query: SampleIndexQuery;

    sortOptions: { id: string, label: string }[];
    pageSizeOptions: number[];
}

const DEFAULTS: EvaluationTransferFormStateModel = {
    orgKey: null,
    sampleKey: null,
    evalKeys: [],
    redirectKey: null,
    data: [],
    sample: null,
    groups: [],
    status: Status.UNINITIALIZED,
    total: 0,
    page: {
        index: 0,
        size: 20
    },
    sort: {
        column: 'updatedAt',
        order: 'desc'
    },
    sortOptions: SAMPLE_SORT_OPTIONS,
    pageSizeOptions: [20,50,100,500,1000],
    query: {
        search: null,
        rootstockCultivars: [],
        scionCultivars: [],
        sites: [],
        birthYear: [],
        birthWeek: [],
        scionCropId: [],
        scionLicensee: [],
        groupKey: [],
        rowIndex: null,
        positionIndex: null,
        hideDestroyed: true,
        excludeYear: false,
        startEvalPeriod: null,
        endEvalPeriod: null,
    },
}

export class InitEvaluationTransfer {
    static readonly type = "[EvalTransfer] Init";
    constructor(
        public orgKey: string,
        public sampleKey: string,
    ) {};
}

export class LoadEvaluationTransfer {
    static readonly type = "[EvalTransfer] Load";
    constructor() {};
}

export class LoadTransferSample {
    static readonly type = "[EvalTransfer] Load Sample";
    constructor() {};
}

export class LoadTransferGroups {
    static readonly type = "[EvalTransfer] Load Groups";
    constructor() {};
}

export class PageEvaluationTransfer {
    static readonly type = "[EvalTransfer] Page";
    constructor(public index: number, public size: number) {};
}

export class SortEvaluationTransfer {
    static readonly type = "[EvalTransfer] Sort";
    constructor(public column: string, public order = 'asc'){}
}

export class SetelectedEvaluations {
    static readonly type = "[EvalTransfer] Set Selected Evaluations";
    constructor(public keys: string[]) {}
}
export class QueryEvaluationTransfer {
    static readonly type = "[EvalTransfer] Query";
    constructor(public query: SampleIndexQuery) {}
}
export class ClearEvaluationTransferFilter {
    static readonly type = "[EvalTransfer] Clear";
    constructor() {}
}

export class SubmitEvaluationTransfer {
    static readonly type = "[EvalTransfer] Submit";
    constructor(public targetKey: string) {}
}


@State({
    name: "evaluation_transfer_form",
    defaults: DEFAULTS
})
@Injectable()
export class EvaluationTransferState {

    @Selector()
    static query(state: EvaluationTransferFormStateModel) {
        return state.query;
    }

    @Selector()
    static groups(state: EvaluationTransferFormStateModel) {
        return state.groups;
    }

    @Selector()
    static sort(state: EvaluationTransferFormStateModel) {
        return state.sort;
    }

    @Selector()
    static redirect(state: EvaluationTransferFormStateModel) {
        return state.redirectKey;
    }

    constructor(
        private _sampleService: SampleService,
        private _groupService: GroupService,
        private _evalService: EvaluationService,
    ) {}

    @Action(InitEvaluationTransfer)
    initEvaluationTransfer(ctx: StateContext<EvaluationTransferFormStateModel>, action: InitEvaluationTransfer) {

        const state = ctx.getState();

        ctx.setState({
            ...DEFAULTS,
            evalKeys: state.evalKeys || [],
            orgKey: action.orgKey,
            sampleKey: action.sampleKey,
            status: Status.LOADING, // TODO maybe make it loading
        });

        return ctx.dispatch(new LoadTransferSample);
    }

    @Action(LoadTransferSample)
    loadTransferSample(ctx: StateContext<EvaluationTransferFormStateModel>, action: LoadTransferSample) {
        const state = ctx.getState();

        const detailRequest: DetailRequest = {
            related: [
                'primaryImage',
                'site',
                'rootstockCultivar',
                'scionCultivar',
                'evals'
            ]
        };

        ctx.patchState({
            status: Status.LOADING,
            sample: null,
        });

        return this._sampleService.get(state.sampleKey, detailRequest)
            .pipe(tap(
                sample => {
                    ctx.patchState({
                        sample: sample
                    });

                    return forkJoin(
                        ctx.dispatch(new LoadEvaluationTransfer),
                        ctx.dispatch(new LoadTransferGroups)
                    );
                },
                error => {
                    ctx.patchState({
                        status: translateCommonErrorStatus(error),
                        sample: null
                    });
                }
            ));
    }

    @Action(LoadEvaluationTransfer)
    loadEvaluationTransfer(ctx: StateContext<EvaluationTransferFormStateModel>, action: LoadEvaluationTransfer) {
        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING
        });

        const detail: DetailRequest = {
            related: [
                'site',
                'rootstockCultivar',
                'scionCultivar',
                'plantSample',
                'primaryImage',
                'latestImage',
                'harvestSamples',
                'tastingSamples',
                'evals'
            ],
            counts: [
                'evals',
                'harvestSamples'
            ]
        };

        return this._sampleService
            .query(this.getCurrentFilter(state), detail)
            .pipe(
                tap(
                    result => {
                        if (result.total > 0) {
                            ctx.patchState({
                                status: Status.OK,
                                data: result.data,
                                total: result.total
                            });
                        } else {
                            ctx.patchState({
                                status: Status.EMPTY,
                                data: [],
                                total: 0
                            });
                        }
                    },
                    err => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(err),
                            data: [],
                            total: 0
                        });
                    }
                )
            );
    }

    @Action(LoadTransferGroups)
    loadTransferGroups(ctx: StateContext<EvaluationTransferFormStateModel>, action: LoadTransferGroups) {
        const state = ctx.getState();

        const groupFilter: Filter = {
            queries: [
                {
                    key: 'ownerOrgKey',
                    mode: FilterQueryMode.EQUALS,
                    value: state.orgKey,
                },
                {
                    key: 'type',
                    mode: FilterQueryMode.EQUALS,
                    value: state.sample.type,
                }
            ],
            sort: {
                column: 'label',
                order: 'asc',
            }
        };

        return this._groupService.query(groupFilter)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            groups: result.data,
                        });
                    },
                    err => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(err),
                            groups: [],
                        });
                    }
                )
            )

    }

    @Action(SetelectedEvaluations)
    setelectedEvaluations(ctx: StateContext<EvaluationTransferFormStateModel>, action: SetelectedEvaluations) {
        ctx.patchState({
            evalKeys: action.keys
        });
    }

    @Action(QueryEvaluationTransfer)
    queryEvaluationTransfer(ctx: StateContext<EvaluationTransferFormStateModel>, action: QueryEvaluationTransfer) {

        const state = ctx.getState();

        ctx.patchState({
            query: action.query,
            page: {
                index: 0,
                size: state.page.size
            }
        });

        return ctx.dispatch(new LoadEvaluationTransfer);

    }

    @Action(PageEvaluationTransfer)
    pageEvaluationTransfer(ctx: StateContext<EvaluationTransferFormStateModel>, action: PageEvaluationTransfer) {
        ctx.patchState({
            page: {
                index: action.index,
                size: action.size
            }
        });

        return ctx.dispatch(new LoadEvaluationTransfer);
    }

    @Action(SortEvaluationTransfer)
    sortEvaluationTransfer(ctx: StateContext<EvaluationTransferFormStateModel>, action: SortEvaluationTransfer) {

        const state = ctx.getState();

        ctx.patchState({
            sort: {
                column: action.column,
                order: action.order
            },
            page: {
                index: 0,
                size: state.page.size
            }
        });

        return ctx.dispatch(new LoadEvaluationTransfer);
    }

    @Action(ClearEvaluationTransferFilter)
    clearEvaluationTransferFilter(ctx: StateContext<EvaluationTransferFormStateModel>, action: ClearEvaluationTransferFilter) {

        ctx.patchState({
            query: DEFAULTS.query,
            page: DEFAULTS.page,
            sort: DEFAULTS.sort
        });

        return ctx.dispatch(new LoadEvaluationTransfer);

    }

    @Action(SubmitEvaluationTransfer)
    submitEvaluationTransfer(ctx: StateContext<EvaluationTransferFormStateModel>, action: SubmitEvaluationTransfer) {
        const state = ctx.getState();

        return this._evalService.transfer(action.targetKey, state.evalKeys, state.orgKey)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            redirectKey: action.targetKey,
                        })
                    },
                    error => {
                        ctx.patchState({
                            redirectKey: state.sampleKey
                        })
                    }
                )
            )
    }

    private getCurrentFilter(state: EvaluationTransferFormStateModel): Filter {

        const fb = new FilterBuilder();

        fb.setPage(state.page);
        fb.setSort(state.sort);

        if (state.orgKey) fb.setQuery('ownerOrgKey', state.orgKey);
        if (state.sample) fb.setQuery('type', state.sample.type);
        if (state.sample.scionCultivar) fb.setQuery('scionCropId', [state.sample.scionCultivar.cropId], FilterQueryMode.IN);
        if (state.sampleKey) fb.setQuery('sampleKey', state.sampleKey, FilterQueryMode.NOT_EQUALS);

        if (Array.isArray(state.query.scionCultivars) && state.query.scionCultivars.length > 0) {
            let keys = state.query.scionCultivars.map(i => i.key);
            fb.setQuery('scionCultivarKey', keys, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.rootstockCultivars) && state.query.rootstockCultivars.length > 0) {
            let keys = state.query.rootstockCultivars.map(i => i.key);
            fb.setQuery('rootstockCultivarKey', keys, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.sites) && state.query.sites.length > 0) {
            let keys = state.query.sites.map(i => i.key);
            fb.setQuery('siteKey', keys, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.birthYear) && state.query.birthYear.length > 0) {
            fb.setQuery('birthYear', state.query.birthYear, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.birthWeek) && state.query.birthWeek.length > 0) {
            fb.setQuery('birthWeek', state.query.birthWeek, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.groupKey) && state.query.groupKey.length > 0) {
            fb.setQuery('groupKey', state.query.groupKey, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.scionLicensee) && state.query.scionLicensee.length > 0) {
            fb.setQuery('scionLicensee', state.query.scionLicensee, FilterQueryMode.IN);
        }

        if (isNumberSet(state.query.rowIndex)) {
            fb.setQuery('rowIndex', parseNumberSet(state.query.rowIndex), FilterQueryMode.IN);
        } else if (isNumberRange(state.query.rowIndex)) {
            fb.setQuery('rowIndex', parseNumberRangeValue(state.query.rowIndex), FilterQueryMode.BETWEEN);
        }

        if (isNumberSet(state.query.positionIndex)) {
            fb.setQuery('positionIndex', parseNumberSet(state.query.positionIndex), FilterQueryMode.IN);
        } else if (isNumberRange(state.query.positionIndex)) {
            fb.setQuery('positionIndex', parseNumberRangeValue(state.query.positionIndex), FilterQueryMode.BETWEEN);
        }

        if(state.query.hideDestroyed) {
            fb.setQuery('deathDate', new Date(), FilterQueryMode.HAS_NO_VALUE);
        }

        if (state.query.excludeYear && (Array.isArray(state.query.birthYear) && state.query.birthYear.length > 0)) {
            fb.setQuery('birthYear', state.query.birthYear, FilterQueryMode.NOT_EQUALS);
        }

        if (state.query.startEvalPeriod && state.query.endEvalPeriod) {
            fb.setQuery('evalPeriod', [state.query.startEvalPeriod, state.query.endEvalPeriod], FilterQueryMode.BETWEEN);
        }

        if (state.query.search) {
            if (state.query.search.trim()) fb.setQuery('$fuzzy', state.query.search);
        }

        return fb.get();
    }

}